import React, { useEffect, useState } from 'react'

import {
  Box,
  BoxProps,
  Dialog,
} from '@material-ui/core'

import { ArrowDropDown, CalendarToday, CalendarViewDay, Schedule } from '../../icons'

import { DateRangeInput, DateRangeInputProps, DateRangeValue } from './DateRangeInput'
import { ReportRange } from '../ReportRange'
import { MenuAbstractionProps, RoundedPlainTextButtonMenu } from '../Menus'


import { dateFnsOps, getFinancialYearStart, getReverseEpochShiftedDate } from '@percept/utils'
import { RoundedPlainTextButton } from '../Buttons'

export type DateRangePreset = (
  | 'current-financial-year'
  | 'current-year'
  | 'last-year'
  | 'last-financial-year'
  | 'last-quarter'
  | 'last-6-months'
  | 'last-3-months'
  | 'last-2-months'
  | 'last-month'
  | 'last-week'
  | 'last-7-days'
  | 'last-30-days'
  | 'yesterday'
  | 'today'
)

const makeMonthRangeCalculator = (amount: number): ((refDate: Date) => [Date, Date]) => (
  (refDate): [Date, Date] => [
    dateFnsOps.startOfMonth(dateFnsOps.subMonths(refDate, amount)),
    dateFnsOps.endOfMonth(dateFnsOps.subMonths(refDate, 1))
  ]
)

export const dateRangeCalculators: Record<DateRangePreset, (referenceDate: Date) => [Date, Date]> = {
  'current-financial-year': refDate => [
    getReverseEpochShiftedDate(getFinancialYearStart(refDate)),
    refDate,
  ],
  'current-year': refDate => [
    dateFnsOps.startOfYear(refDate), refDate,
  ],
  'last-financial-year': refDate => {
    const fyStart = getReverseEpochShiftedDate(getFinancialYearStart(dateFnsOps.subYears(refDate, 1)))
    return [
      fyStart,
      dateFnsOps.subDays(dateFnsOps.addYears(fyStart, 1), 1),
    ]
  },
  'last-year': refDate => [
    dateFnsOps.startOfYear(dateFnsOps.subYears(refDate, 1)), dateFnsOps.endOfYear(dateFnsOps.subYears(refDate, 1))
  ],
  'last-quarter': refDate => [
    dateFnsOps.startOfQuarter(dateFnsOps.subQuarters(refDate, 1)), dateFnsOps.endOfQuarter(dateFnsOps.subQuarters(refDate, 1))
  ],
  'last-6-months': makeMonthRangeCalculator(6),
  'last-3-months': makeMonthRangeCalculator(3),
  'last-2-months': makeMonthRangeCalculator(2),
  'last-month': makeMonthRangeCalculator(1),
  'last-week': refDate => [
    dateFnsOps.startOfWeek(dateFnsOps.subWeeks(refDate, 1), { weekStartsOn: 1 }),
    dateFnsOps.endOfWeek(dateFnsOps.subWeeks(refDate, 1), { weekStartsOn: 1 })
  ],
  'today': () => {
    const today = new Date()
    return [today, today] 
  },
  'yesterday': () => {
    const yesterday = dateFnsOps.subDays(new Date(), 1)
    return [yesterday, yesterday]
  },
  'last-7-days': refDate => [
    dateFnsOps.subDays(refDate, 6), refDate,
  ],
  'last-30-days': refDate => [
    dateFnsOps.subDays(refDate, 29), refDate,
  ]
}


export type DateRangePresetOption = DateRangePreset | 'custom'

export const defaultDateRangePresetOptions: DateRangePresetOption[] = [
  'current-financial-year',
  'current-year',
  'last-financial-year',
  'last-year',
  'last-quarter',
  'last-month',
  'last-week',
  'custom',
]


export const dateRangePresetLabels: Record<DateRangePresetOption, string> = {
  'custom': 'Custom',
  'current-financial-year': 'Current Financial Year',
  'current-year': 'Current Year',
  'last-financial-year': 'Last Financial Year',
  'last-year': 'Last Year',
  'last-quarter': 'Last Quarter',
  'last-6-months': 'Last 6 Months',
  'last-3-months': 'Last 3 Months',
  'last-2-months': 'Last 2 Months',
  'last-month': 'Last Month',
  'last-week': 'Last Week',
  'last-30-days': 'Last 30 Days',
  'last-7-days': 'Last 7 Days',
  'today': 'Today',
  'yesterday': 'Yesterday',
}

export const resolveDateRangePresetLabel = (
  dateRangePreset: DateRangePresetOption,
  dateRange: DateRangeValue,
): React.ReactNode => (
  dateRangePreset === 'custom' ?
    <ReportRange
      start={dateRange[0]}
      end={dateRange[1]}
      TypographyProps={{
        variant: 'inherit',
      }} /> :
    dateRangePresetLabels[dateRangePreset]
)


export type DateRangePresetInputProps = (
  Omit<DateRangeInputProps, 'onChange' | 'maxDate'> & {
    maxDate: Date | null
    dateRangePreset: DateRangePresetOption
    dateRangePresetOptions?: DateRangePresetOption[]
    onChange: (value: [Date, Date], dateRangePreset: DateRangePresetOption) => void
    BoxProps?: BoxProps
    TriggerProps?: MenuAbstractionProps<{}>['TriggerProps']
  }
)

export const DateRangePresetInput = ({
  dateRangePreset,
  value,
  dateRangePresetOptions = defaultDateRangePresetOptions,
  onChange,
  color,
  maxDate,
  BoxProps = {},
  ...props
}: DateRangePresetInputProps): JSX.Element => {
  return (
    <Box {...BoxProps}>
      <Box mb={3}>
        <RoundedPlainTextButtonMenu
          TriggerProps={{
            variant: 'contained',
            size: 'small',
            startIcon: <CalendarToday />,
            endIcon: <ArrowDropDown />,
          }}
          value={dateRangePreset}
          label={dateRangePresetLabels[dateRangePreset]}
          onChange={(e, preset: DateRangePreset | 'custom'): void => {
            const existingPreset = dateRangePreset
            let newRange = value
            if( preset !== 'custom' && maxDate ){
              newRange = dateRangeCalculators[preset](maxDate)
            }else if( existingPreset !== 'custom' && maxDate){
              newRange = dateRangeCalculators[existingPreset](maxDate)
            }
            onChange(newRange as [Date, Date], preset)
          }}
          options={
            dateRangePresetOptions.map( value => ({
              value,
              label: dateRangePresetLabels[value],
            }))
          } />
      </Box>
      <DateRangeInput
        variant='static'
        divided
        color={color}
        value={value}
        maxDate={maxDate}
        onChange={(dateRange): void => {
          onChange([new Date(dateRange[0]), new Date(dateRange[1])], 'custom')
        }}
        {...props} />
    </Box>
  )
}


export const InlineDateRangePresetInput = ({
  dateRangePreset,
  value,
  dateRangePresetOptions = defaultDateRangePresetOptions,
  onChange,
  color,
  maxDate,
  BoxProps = {},
  TriggerProps,
  ...props
}: DateRangePresetInputProps): JSX.Element => {
  const [open, setOpen] = useState(false)
  const [localValue, setLocalValue] = useState<DateRangeValue>(value)

  useEffect(() => {
    if( !open ){
      setLocalValue(value)
    }
  }, [value, open])

  const handleClose = (): void => {
    setOpen(false)
    setLocalValue(value)
  }
  return (
    <>
      <RoundedPlainTextButtonMenu
        TriggerProps={{
          variant: 'contained',
          size: 'small',
          endIcon: <ArrowDropDown />,
          ...TriggerProps,
        }}
        value={dateRangePreset}
        label={
          dateRangePreset === 'custom' ?
            <ReportRange disableTypography start={value[0]} end={value[1]} /> :
            dateRangePresetLabels[dateRangePreset]
        }
        onChange={(e, preset: DateRangePreset | 'custom'): void => {
          if( preset === 'custom' ){
            setOpen(true)
            return
          }
          const existingPreset = dateRangePreset
          let newRange = value
          if( maxDate ){
            newRange = dateRangeCalculators[preset](maxDate)
          }else if( existingPreset !== 'custom' && maxDate){
            newRange = dateRangeCalculators[existingPreset](maxDate)
          }
          onChange(newRange as [Date, Date], preset)
        }}
        options={
          dateRangePresetOptions.map( value => ({
            value,
            label: dateRangePresetLabels[value],
          }))
        } />
    <Dialog
      open={open}
      maxWidth='md'
      onClose={handleClose}>
      <DateRangeInput
        key={`${localValue[0]}-${localValue[1]}`}
        variant='static'
        open={open}
        divided
        color={color}
        value={localValue}
        maxDate={maxDate}
        onChange={(dateRange): void => {
          setLocalValue([new Date(dateRange[0]), new Date(dateRange[1])])
        }}
        {...props} />
      <Box p={2} display='flex' justifyContent='flex-end' alignItems='center'>
        <RoundedPlainTextButton
          variant='contained'
          onClick={handleClose}>
          Cancel
        </RoundedPlainTextButton>
        <Box ml={2}>
          <RoundedPlainTextButton
            variant='contained'
            color='primary'
            onClick={(): void => {
              onChange([new Date(localValue[0]), new Date(localValue[1])], 'custom')
              handleClose()
            }}>
            Apply
          </RoundedPlainTextButton>
        </Box>
      </Box>
    </Dialog>
    </>
  )
}